import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { ChevronDown, Check } from 'lucide-react';
import { specializationService } from '../../../services/specializationService';

const ProfileSetupForm = ({ formData, onChange, onNext, onSkip }) => {
  const [logoPreview, setLogoPreview] = useState(formData?.clinicLogo || null);
  const [photoPreview, setPhotoPreview] = useState(formData?.professionalPhoto || null);
  const [errors, setErrors] = useState({});

  const [open, setOpen] = useState(false);
  const [specialtyOptions, setSpecialtyOptions] = useState([]);
  const [selectedSpecialties, setSelectedSpecialties] = useState([]);
  const [loadingSpecialties, setLoadingSpecialties] = useState(true);


  const handleFileChange = (e, type) => {
    const file = e?.target?.files?.[0];
    if (file) {
      if (file?.size > 5000000) {
        setErrors((prev) => ({
          ...prev,
          [type]: 'Arquivo muito grande. Máximo 5MB.',
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader?.result;
        if (type === 'logo') {
          setLogoPreview(result);
          onChange({ target: { name: 'clinicLogo', value: file } });
        } else {
          setPhotoPreview(result);
          onChange({ target: { name: 'professionalPhoto', value: file } });
        }
        setErrors((prev) => ({ ...prev, [type]: '' }));
      };
      reader?.readAsDataURL(file);
    }
  };

useEffect(() => {
  async function loadSpecialties() {
    try {
      setLoadingSpecialties(true);
      const { data, error } = await specializationService.getAllSpecializations();
      if (error || !data) throw new Error("Falha ao carregar");
      
      const options = data.map(item => ({
        value: String(item.id),
        label: item.description
      }));
      setSpecialtyOptions(options);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingSpecialties(false);
    }
  }
  loadSpecialties();
}, []);

useEffect(() => {
  if (!specialtyOptions.length) return;

  const specs = formData?.specializations;
  let initial = [];

  if (Array.isArray(specs)) {
    initial = [...specs];
  } else if (typeof specs === 'string' && specs.trim()) {
    initial = specs.split(',').map(s => s.trim()).filter(Boolean);
  }

  const valid = initial.filter(v => 
    specialtyOptions.some(opt => opt.value === String(v))
  );

  setSelectedSpecialties(valid);
}, [formData?.specializations, specialtyOptions]);


//const handleSpecialtyToggle = (value) => {  
//  setSelectedSpecialties(prev => {
//    if (prev.includes(value)) {
//      return prev.filter(v => v !== value);
//    } else {
//      return [...prev, value];  // value é o ID!
//    }
//  });
//};

  // Função de toggle atualizada para atualizar o pai via onChange
  const handleSpecialtyToggle = (value) => {
    setSelectedSpecialties(prev => {
      const newSelected = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];

      // Atualiza o estado no componente pai (OnboardingFlow)
      onChange({
        target: {
          name: 'specializations',
          value: newSelected
        }
      });

      return newSelected;
    });
  };


  const validateWhatsApp = (number) => {
    // Remove all non-digit characters
    const cleaned = number?.replace(/\D/g, '');
    
    // Accept two formats:
    // 1. With country code: +55 (DDD) 9XXXX-XXXX (13 digits total)
    // 2. Without country code: (DDD) 9XXXX-XXXX (11 digits total)
    
    // Check for format WITH country code (55 + 11 digits)
    if (cleaned?.length === 13) {
      // Should start with country code 55 for Brazil
      if (!cleaned?.startsWith('55')) {
        return false;
      }
      // Mobile number should start with 9 (after area code)
      const mobileNumber = cleaned?.substring(4);
      if (mobileNumber?.charAt(0) !== '9') {
        return false;
      }
      return true;
    }
    
    // Check for format WITHOUT country code (11 digits)
    if (cleaned?.length === 11) {
      // Mobile number should start with 9 (after area code)
      if (cleaned?.charAt(2) !== '9') {
        return false;
      }
      return true;
    }
    
    return false;
  };

  const formatWhatsApp = (value) => {
    const numbers = value?.replace(/\D/g, '');
    
    // Determine if user is entering with or without country code
    const hasCountryCode = numbers?.startsWith('55') && numbers?.length > 11;
    
    if (hasCountryCode) {
      // Format: +55 (XX) 9XXXX-XXXX
      if (numbers?.length <= 2) {
        return `+${numbers}`;
      } else if (numbers?.length <= 4) {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2)}`;
      } else if (numbers?.length <= 9) {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2, 4)}) ${numbers?.slice(4)}`;
      } else {
        return `+${numbers?.slice(0, 2)} (${numbers?.slice(2, 4)}) ${numbers?.slice(4, 9)}-${numbers?.slice(9, 13)}`;
      }
    } else {
      // Format: (XX) 9XXXX-XXXX (without country code)
      if (numbers?.length <= 2) {
        return `(${numbers}`;
      } else if (numbers?.length <= 7) {
        return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2)}`;
      } else {
        return `(${numbers?.slice(0, 2)}) ${numbers?.slice(2, 7)}-${numbers?.slice(7, 11)}`;
      }
    }
  };

  const formatCRP = (value) => {
    // Remove tudo que não é número
    const numbers = value?.replace(/\D/g, '') || '';

    // CRP tem no máximo 8 dígitos: 2 para região + 6 para sequência
    // Mas aceita até 8 para evitar cortes abruptos
    const cleaned = numbers.slice(0, 8);

    if (cleaned.length <= 2) {
      // Apenas os dois primeiros dígitos (código da região)
      return cleaned;
    } else {
      // Adiciona a barra após os 2 primeiros dígitos
      const region = cleaned.slice(0, 2);
      const sequence = cleaned.slice(2);
      return `${region}/${sequence}`;
    }
  };

  const handleWhatsAppChange = (e) => {
    const formatted = formatWhatsApp(e?.target?.value);
    onChange({ target: { name: 'whatsappNumber', value: formatted } });
  };

  const handleCrpChange = (e) => {
    const formatted = formatCRP(e?.target?.value);
    onChange({ target: { name: 'crp', value: formatted } });
  };  

  const validateForm = () => {
    const newErrors = {};

    // Validate full name (psychologist's full name)
    if (!formData?.fullName || formData?.fullName?.trim()?.length < 3) {
      newErrors.fullName = 'Nome completo deve ter no mínimo 3 caracteres';
    }

    // Validate display name (psychologist's display name)
    if (!formData?.displayName || formData?.displayName?.trim()?.length < 3) {
      newErrors.displayName = 'Nome de exibição deve ter no mínimo 3 caracteres';
    }    

    // Validate clinic name
    if (!formData?.clinicName || formData?.clinicName?.trim()?.length < 3) {
      newErrors.clinicName = 'Nome da clínica deve ter no mínimo 3 caracteressss';
    }
    
    // Validate specialty (at least one must be selected)
    if (selectedSpecialties.length === 0) {
      newErrors.specializations = 'Selecione pelo menos uma especialidade';
    }

    // Validate CRP
    if (!formData?.crp || !/^\d{2}\/\d{4,6}$/?.test(formData?.crp)) {
      newErrors.crp = 'CRP inválido. Use o formato: 06/12345';
    }

    // Validate phone (optional but if provided, must be valid)
    if (formData?.phone && formData?.phone?.replace(/\D/g, '')?.length < 10) {
      newErrors.phone = 'Telefone inválido';
    }

    // Validate WhatsApp (required, accepts both formats)
    if (!formData?.whatsappNumber) {
      newErrors.whatsappNumber = 'WhatsApp é obrigatório';
    } else if (!validateWhatsApp(formData?.whatsappNumber)) {
      newErrors.whatsappNumber = 'WhatsApp inválido. Use: +55 (XX) 9XXXX-XXXX ou (XX) 9XXXX-XXXX';
    }

    setErrors(newErrors);

    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onNext();
    }
  };

  const formatPhone = (value) => {
    const numbers = value?.replace(/\D/g, '');
    if (numbers?.length <= 11) {
      return numbers?.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }
    return value;
  };

  const handlePhoneChange = (e) => {
    const formatted = formatPhone(e?.target?.value);
    onChange({ target: { name: 'phone', value: formatted } });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-xl p-6 border border-primary/20 mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <Icon name="User" size={24} color="#FFFFFF" />
          </div>
          <div>
            <h3 className="text-xl font-semibold text-foreground mb-2">
              Primeiro Configure Seu Perfil
            </h3>
            <p className="text-muted-foreground">
              Preencha seus dados de psicólogo e informações da sua clínica.<br />
              Algumas dessas informações serão usadas na sua página de agendamento pública.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="User" size={20} color="var(--color-primary)" />
          Informações Pessoais
        </h3>

        <div className="space-y-4">
          <Input
            label="Nome Completo do Psicólogo"
            type="text"
            name="fullName"
            placeholder="João Silva"
            description="Seu nome completo, que será usado para cadastro"
            value={formData?.fullName}
            onChange={onChange}
            error={errors?.fullName}
            required
          />

          <Input
            label="Nome de exibição"
            type="text"
            name="displayName"
            placeholder="Dr. Silva"
            description="Seu nome de exibição, como será visto publicamente"
            value={formData?.displayName}
            onChange={onChange}
            error={errors?.displayName}
            required
          />

          <Input
            label="CRP (Conselho Regional de Psicologia)"
            type="text"
            name="crp"
            placeholder="06/12345"
            description="Formato: UF/Número (ex: 06/12345)"
            value={formData?.crp}
            onChange={handleCrpChange}
            error={errors?.crp}
            required
          />
        </div>
      </div>

      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Building2" size={20} color="var(--color-primary)" />
          Informações da Clínica
        </h3>

        <div className="space-y-4">
          <Input
            label="Nome da Clínica"
            type="text"
            name="clinicName"
            placeholder="Clínica de Psicologia Bem-Estar"
            description="Nome oficial da sua clínica"
            value={formData?.clinicName}
            onChange={onChange}
            error={errors?.clinicName}
            required
          />
 
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            <div>
              <br />
              <label className="block text-sm font-medium text-foreground mb-2">
                Foto de Perfil
              </label>
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border">
                  {photoPreview ? (
                    <Image
                      src={photoPreview}
                      alt="Foto profissional circular do psicólogo em traje formal"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Icon name="User" size={32} color="var(--color-muted-foreground)" />
                  )}
                </div>
                <div className="flex-1">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'photo')}
                    className="hidden"
                    id="photo-upload"
                  />
                  <label htmlFor="photo-upload">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      iconName="Upload"
                      iconPosition="left"
                      onClick={() => document.getElementById('photo-upload')?.click()}
                    >
                      Carregar Foto
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG até 5MB
                  </p>
                  {errors?.photo && (
                    <p className="text-xs text-error mt-1">{errors?.photo}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>


              {/* Especialidades */}

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-3">
                    Especialidades <span className="text-error">*</span>
                  </label>
                  <p className="text-xs text-muted-foreground mb-3">
                    Você pode selecionar múltiplas especialidades
                  </p>

                  {/* Multi-Select Custom - Substituindo o Select antigo */}
                  <div className="relative">
                    {/* Botão principal do dropdown */}
                    <button
                      type="button"
                      onClick={() => setOpen(!open)}
                      className={`flex items-center justify-between w-full p-3 rounded-lg border-2 transition-all duration-200 ${
                        selectedSpecialties.length > 0
                          ? 'border-primary bg-primary/5'
                          : errors?.specializations 
                          ? 'border-error bg-error/5'
                          : 'border-border bg-card hover:border-primary/50'
                      }`}
                    >
                      <span className="text-sm text-foreground">
                        {selectedSpecialties.length === 0
                          ? 'Selecione suas especialidades'
                          : `${selectedSpecialties.length} especialidade(s) selecionada(s)`}
                      </span>
                      <ChevronDown
                        className={`w-5 h-5 text-foreground transition-transform duration-200 ${
                          open ? 'rotate-180' : ''
                        }`}
                      />
                    </button>

                    {/* Mensagem de erro (se houver) */}
                    {errors?.specializations && (
                      <p className="text-sm text-error mt-2">{errors.specializations}</p>
                    )}

                    {/* Dropdown com opções */}
                    {open && (
                      <>
                        <div className="absolute z-10 mt-2 w-full overflow-auto rounded-lg border-2 border-border bg-card shadow-lg max-h-60">
                          {specialtyOptions?.map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => handleSpecialtyToggle(option.value)}
                              className="flex items-center gap-3 w-full px-3 py-2.5 hover:bg-primary/10 transition-colors text-left"
                            >
                              <div
                                className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                  selectedSpecialties.includes(option.value)
                                    ? 'border-primary bg-primary'
                                    : 'border-border bg-transparent'
                                }`}
                              >
                                {selectedSpecialties.includes(option.value) && (
                                  <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />
                                )}
                              </div>
                              <span className="text-sm text-foreground">{option.label}</span>
                            </button>
                          ))}
                        </div>

                        {/* Overlay para fechar ao clicar fora */}
                        <div
                          className="fixed inset-0 z-0"
                          onClick={() => setOpen(false)}
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>




      <div className="bg-card rounded-xl p-6 border border-border">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="Phone" size={20} color="var(--color-primary)" />
          Contato
        </h3>

        <div className="space-y-4">
          <Input
            label="WhatsApp para Contato"
            type="tel"
            name="whatsappNumber"
            placeholder="+55 (11) 98765-4321 ou (11) 98765-4321"
            description="Com ou sem código do país. Usado para notificações e contato com pacientes"
            value={formData?.whatsappNumber}
            onChange={handleWhatsAppChange}
            error={errors?.whatsappNumber}
            required
          />

        </div>
      </div>

      <div className="flex items-center justify-between pt-4">
        <Button type="button" variant="ghost" onClick={onSkip}>
          Pular esta etapa
        </Button>
        <Button
          type="submit"
          variant="default"
          iconName="ChevronRight"
          iconPosition="right"
        >
          Continuar
        </Button>
      </div>
    </form>
  );
};

export default ProfileSetupForm;